import { labelId, labelRegion, labelArea, labelCentro } from '../../../../../../features/labels/labels';
import { DIMENSION_META, shortKey } from '../feederScoreShared';
import {
  attentionPctForCell,
  buildHeaderSummary,
  buildRangeRanking,
} from '../feederScoreObservabilityUtils';
import { parseScappLocalDate } from '../../../../utils/parseScappDate';

const tr = (translate, key, fallback) => {
  const value = typeof translate === 'function' ? translate(key) : key;
  return value && value !== key ? value : fallback;
};

const LEVEL_LABELS = {
  centro: ['fs_cage', 'Jaula'],
  area: ['center_label', 'Centro'],
  region: ['area', 'Área'],
};

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const rowName = (view, rowId) => view.nameById?.[rowId] || String(rowId).split('-').pop() || rowId;
const entityHeader = (level, translate) => {
  const [key, fallback] = LEVEL_LABELS[level] || LEVEL_LABELS.centro;
  return tr(translate, key, fallback);
};

const commonColumns = (level, translate) => [
  { key: 'level', header: tr(translate, 'export_column_level', 'Nivel'), width: 14 },
  { key: 'id', header: tr(translate, 'export_column_entity_id', 'Id entidad'), width: 28 },
  { key: 'entity', header: entityHeader(level, translate), width: 22 },
  { key: 'period', header: tr(translate, 'export_column_period', 'Periodo'), width: 18 },
  { key: 'granularity', header: tr(translate, 'export_column_granularity', 'Granularidad'), width: 16 },
];

const identity = (view, rowId, col, level, groupMode, translate) => ({
  level: tr(translate, `export_level_${level}`, level),
  id: rowId,
  columnKey: col.key,
  entity: rowName(view, rowId),
  period: col.label,
  granularity: tr(translate, `fs_gran_${groupMode}`, groupMode),
});

const matrixRows = (view, level, mode) => view.rows.map((rowId) => {
  let numerator = 0;
  let denominator = 0;
  let scoreSum = 0;
  let scoreCount = 0;
  let alarmTotal = 0;
  let alarmAttended = 0;
  const row = { entity: rowName(view, rowId) };
  view.columns.forEach((col, index) => {
    const cell = view.data?.[col.key]?.[rowId];
    if (mode === 'attention') {
      const total = Math.max(0, number(cell?.alarmas_total) || 0);
      const unattended = Math.max(0, number(cell?.alarmas_no_atendidas) || 0);
      row[`p${index}`] = attentionPctForCell(cell);
      alarmTotal += total;
      alarmAttended += Math.max(0, total - unattended);
      return;
    }
    const score = number(cell?.score);
    const food = Math.max(0, number(cell?.delivered_food_kg) || 0);
    row[`p${index}`] = score;
    if (score != null) {
      scoreSum += score;
      scoreCount += 1;
      if (food > 0) { numerator += score * food; denominator += food; }
    }
  });
  if (mode === 'attention') row.range = alarmTotal ? (alarmAttended / alarmTotal) * 100 : null;
  else {
    row.average = scoreCount ? scoreSum / scoreCount : null;
    row.weighted = denominator ? numerator / denominator : null;
  }
  return row;
});

const summarySheet = (mode, summary, translate) => {
  const integerKeys = new Set(['periods', 'scoredRows', 'totalScores', 'alarmTotal', 'alarmAttended']);
  const percentageKeys = new Set(['alarmAttendedPct']);
  const scoreRows = [
    ['periods', 'export_summary_periods', 'Períodos analizados'],
    ['scoredRows', 'export_summary_entities', 'Entidades con puntaje'],
    ['totalScores', 'export_summary_scores', 'Puntuaciones totales'],
    ['scoreWeighted', 'export_summary_weighted', 'Score ponderado'],
    ['scoreMean', 'export_summary_average', 'Promedio plano'],
    ['scoreMin', 'export_summary_minimum', 'Score mínimo'],
    ['scoreMax', 'export_summary_maximum', 'Score máximo'],
  ];
  const attentionRows = [
    ['periods', 'export_summary_periods', 'Períodos analizados'],
    ['alarmTotal', 'fs_header_total_alarms', 'Alarmas totales'],
    ['alarmAttended', 'fs_header_attended_alarms', 'Alarmas atendidas'],
    ['alarmAttendedPct', 'alarm_attention_ranking_attention', '% atención'],
    ['reactionMean', 'export_column_reaction_mean', 'Reacción media (s)'],
    ['reactionP95', 'export_column_reaction_p95', 'Reacción p95 (s)'],
  ];
  return {
    name: tr(translate, 'summary', 'Resumen'),
    columns: [
      { key: 'metric', header: tr(translate, 'export_column_metric', 'Métrica'), width: 32 },
      { key: 'value', header: tr(translate, 'value', 'Valor'), width: 24 },
    ],
    rows: (mode === 'attention' ? attentionRows : scoreRows)
      .map(([key, labelKey, fallback]) => ({
        metric: tr(translate, labelKey, fallback),
        value: { v: summary[key], s: integerKeys.has(key) ? 3 : percentageKeys.has(key) ? 4 : 2 },
      })),
  };
};

const rankingSheet = (name, ranking, view, level, translate, mode) => ({
  name,
  columns: [
    { key: 'position', header: tr(translate, 'export_column_position', 'Posición'), width: 12, style: 3 },
    { key: 'entity', header: entityHeader(level, translate), width: 24 },
    ...(mode === 'reaction' ? [
      { key: 'value', header: tr(translate, 'export_column_reaction_mean', 'Reacción media (s)'), width: 22, style: 2 },
      { key: 'samples', header: tr(translate, 'export_column_reaction_samples', 'Muestras'), width: 14, style: 3 },
    ] : mode === 'attention' ? [
      { key: 'value', header: tr(translate, 'alarm_attention_ranking_attention', '% atención'), width: 18, style: 4 },
      { key: 'total', header: tr(translate, 'fs_header_total_alarms', 'Alarmas totales'), width: 18, style: 3 },
      { key: 'unattended', header: tr(translate, 'export_column_unattended_alarms', 'Alarmas no atendidas'), width: 24, style: 3 },
    ] : [
      { key: 'value', header: tr(translate, 'export_column_average_score', 'Score promedio'), width: 18, style: 2 },
    ]),
  ],
  rows: ranking.map((item, index) => ({
    position: index + 1,
    entity: rowName(view, item.rowId),
    value: item.value?.score,
    total: item.value?.alarmas_total,
    unattended: item.value?.alarmas_no_atendidas,
    samples: item.value?.reaction_time?.n,
  })),
});

const dimensions = (view, translate) => view.columns.flatMap((col) => view.rows.flatMap((rowId) => {
  const cell = view.data?.[col.key]?.[rowId];
  return (cell?.cycles || []).flatMap((cycle, cycleIndex) => Object.entries(cycle?.dims || {}).map(([key, dim]) => {
    const applicable = dim?.applicable !== false;
    const q = Math.max(0, Math.min(1, number(dim?.q) ?? (applicable ? 0 : 1)));
    const weight = number(dim?.w) ?? DIMENSION_META[key]?.defaultWeight ?? 0;
    const pointsLost = applicable ? Math.max(0, (100 - (number(cell?.base) || 0)) * weight / 100 * (1 - q)) : 0;
    const meta = DIMENSION_META[key];
    return {
      entity: rowName(view, rowId),
      period: col.label,
      visit: cycle.cycle_id || cycleIndex + 1,
      dimension: meta?.labelKey ? tr(translate, meta.labelKey, shortKey(key)) : shortKey(key),
      weight,
      achievement: q * 100,
      pointsLost,
      applicable,
      code: dim?.reason?.code || '',
      reason: dim?.detalle || dim?.reason?.detalle || dim?.reason?.code || (typeof dim?.reason === 'string' ? dim.reason : ''),
    };
  }));
}));

const attentionEvents = (view, translate) => view.columns.flatMap((col) => view.rows.flatMap((rowId) => {
  const cell = view.data?.[col.key]?.[rowId];
  return (cell?.cycles || []).flatMap((cycle, cycleIndex) => (
    (cycle?.dims?.alarm?.eventos || []).map((event) => ({
      entity: rowName(view, rowId),
      period: col.label,
      visit: cycle.cycle_id || cycleIndex + 1,
      time: event.t ? parseScappLocalDate(event.t) : null,
      type: event.type || tr(translate, 'alarms', 'Alarma'),
      detail: event.detalle || '',
      attended: event.atendido,
      natural: event.recuperacion_natural,
    }))
  ));
}));

const reportInfo = ({ mode, translate, metadata, level, groupMode, recalcParams }) => ({
  name: tr(translate, 'export_sheet_information', 'Información del reporte'),
  columns: [
    { key: 'field', header: tr(translate, 'export_column_field', 'Campo'), width: 30 },
    { key: 'value', header: tr(translate, 'value', 'Valor'), width: 60 },
  ],
  rows: [
    { field: tr(translate, 'export_column_menu', 'Menú'), value: tr(translate, mode === 'attention' ? 'alarm_attention_menu' : 'feeder_score_menu', mode) },
    { field: tr(translate, 'region', 'Región'), value: labelRegion(metadata.region, metadata.region) },
    { field: tr(translate, 'area', 'Área'), value: labelArea({ region: metadata.region, area: metadata.area }, metadata.area) },
    { field: tr(translate, 'center_label', 'Centro'), value: labelCentro({ region: metadata.region, area: metadata.area, centro: metadata.center }, metadata.center) },
    { field: tr(translate, 'export_column_level', 'Nivel'), value: level },
    { field: tr(translate, 'temporal_range', 'Rango temporal'), value: `${metadata.dias?.startDate || ''} - ${metadata.dias?.endDate || ''}` },
    { field: tr(translate, 'export_column_granularity', 'Granularidad'), value: tr(translate, `fs_gran_${groupMode}`, groupMode) },
    { field: tr(translate, 'export_column_recalculation', 'Recálculo exploratorio'), value: mode === 'attention' ? tr(translate, 'export_attention_not_recalculated', 'No afecta estas métricas') : Boolean(recalcParams) },
    { field: tr(translate, 'export_column_user', 'Usuario'), value: typeof localStorage === 'undefined' ? '' : localStorage.getItem('username') || '' },
    { field: tr(translate, 'export_column_generated', 'Generado'), value: new Date() },
    { field: tr(translate, 'export_column_version', 'Versión del exportador'), value: 'scapp-xlsx-1' },
  ],
});

export function buildFeederScoreSheets({
  mode = 'score',
  view,
  nivelSeleccionado = 'centro',
  groupMode = 'day',
  recalcParams = null,
  translate,
  metadata = {},
}) {
  const displayId = (id, fallback) => nivelSeleccionado === 'centro' ? fallback
    : labelId(id, nivelSeleccionado === 'region' ? 'ra' : 'rac', fallback);
  view = { ...view, nameById: Object.fromEntries(view.rows.map((id) => [id, displayId(id, rowName(view, id))])) };
  const summary = buildHeaderSummary(view.columns, view.rows, view.data);
  const periodColumns = view.columns.map((col, index) => ({
    key: `p${index}`, header: col.label, width: 15, style: mode === 'attention' ? 4 : 2,
  }));
  const sheets = [
    summarySheet(mode, summary, translate),
    {
      name: tr(translate, mode === 'attention' ? 'alarm_attention_menu' : 'feeder_score_menu', mode === 'attention' ? 'Atención de alarmas' : 'Feeder Score'),
      columns: [
        { key: 'entity', header: entityHeader(nivelSeleccionado, translate), width: 24 },
        ...periodColumns,
        ...(mode === 'attention'
          ? [{ key: 'range', header: tr(translate, 'export_column_range_attention', 'Atención del rango'), width: 20, style: 4 }]
          : [
            { key: 'average', header: tr(translate, 'average', 'Promedio'), width: 16, style: 2 },
            { key: 'weighted', header: tr(translate, 'export_column_food_weighted', 'Ponderado por alimento'), width: 24, style: 2 },
          ]),
      ],
      rows: matrixRows(view, nivelSeleccionado, mode),
      options: { freeze: 'B2' },
    },
  ];

  const detailRows = view.columns.flatMap((col) => view.rows.map((rowId) => {
    const cell = view.data?.[col.key]?.[rowId];
    const common = identity(view, rowId, col, nivelSeleccionado, groupMode, translate);
    if (mode === 'attention') {
      const total = Math.max(0, number(cell?.alarmas_total) || 0);
      const unattended = Math.max(0, number(cell?.alarmas_no_atendidas) || 0);
      return {
        ...common,
        total,
        attended: Math.max(0, total - unattended),
        unattended,
        attention: attentionPctForCell(cell),
        mean: number(cell?.reaction_time?.mean_sec),
        p95: number(cell?.reaction_time?.p95_sec),
        max: number(cell?.reaction_time?.max_sec),
        samples: number(cell?.reaction_time?.n),
      };
    }
    return {
      ...common,
      score: number(cell?.score),
      status: cell?.status === 'fasted' ? tr(translate, 'fs_status_fasted', 'Ayuno') : cell?.status,
      days: number(cell?.n_days),
      visits: number(cell?.n_cycles),
      food: number(cell?.delivered_food_kg),
      q: number(cell?.Q),
      exploratory: Boolean(cell?.recalc_exploratory),
      warning: cell?.recalc_warning || '',
    };
  }));
  sheets.push({
    name: tr(translate, mode === 'attention' ? 'export_sheet_attention_detail' : 'export_sheet_scores', mode === 'attention' ? 'Atención (detalle)' : 'Puntajes'),
    columns: [
      ...commonColumns(nivelSeleccionado, translate),
      ...(mode === 'attention' ? [
        { key: 'total', header: tr(translate, 'fs_header_total_alarms', 'Alarmas totales'), width: 18, style: 3 },
        { key: 'attended', header: tr(translate, 'fs_header_attended_alarms', 'Alarmas atendidas'), width: 20, style: 3 },
        { key: 'unattended', header: tr(translate, 'export_column_unattended_alarms', 'Alarmas no atendidas'), width: 24, style: 3 },
        { key: 'attention', header: tr(translate, 'alarm_attention_ranking_attention', '% atención'), width: 16, style: 4 },
        { key: 'mean', header: tr(translate, 'export_column_reaction_mean', 'Reacción media (s)'), width: 22, style: 2 },
        { key: 'p95', header: tr(translate, 'export_column_reaction_p95', 'Reacción p95 (s)'), width: 22, style: 2 },
        { key: 'max', header: tr(translate, 'export_column_reaction_max', 'Reacción máxima (s)'), width: 22, style: 2 },
        { key: 'samples', header: tr(translate, 'export_column_reaction_samples', 'Muestras'), width: 14, style: 3 },
      ] : [
        { key: 'score', header: 'Score', width: 14, style: 2 },
        { key: 'status', header: tr(translate, 'export_column_status', 'Estado'), width: 16 },
        { key: 'days', header: tr(translate, 'export_column_days', 'Días'), width: 12, style: 3 },
        { key: 'visits', header: tr(translate, 'visits', 'Visitas'), width: 14, style: 3 },
        { key: 'food', header: `${tr(translate, 'delivered_food', 'Alimento entregado')} (kg)`, width: 24, style: 2 },
        { key: 'q', header: 'Q', width: 12, style: 2 },
        { key: 'exploratory', header: tr(translate, 'export_column_recalculation', 'Recálculo exploratorio'), width: 24 },
        { key: 'warning', header: tr(translate, 'export_column_warning', 'Advertencia'), width: 28 },
      ]),
    ],
    rows: detailRows,
  });

  if (mode === 'score') {
    const dimensionRows = dimensions(view, translate);
    if (dimensionRows.length) sheets.push({
      name: tr(translate, 'export_sheet_score_audit', 'Detalle del puntaje'),
      columns: [
        { key: 'entity', header: entityHeader(nivelSeleccionado, translate), width: 22 },
        { key: 'period', header: tr(translate, 'export_column_period', 'Periodo'), width: 18 },
        { key: 'visit', header: tr(translate, 'export_column_visit', 'Visita'), width: 14 },
        { key: 'dimension', header: tr(translate, 'export_column_dimension', 'Dimensión'), width: 24 },
        { key: 'weight', header: tr(translate, 'export_column_weight', 'Peso'), width: 14, style: 2 },
        { key: 'achievement', header: tr(translate, 'export_column_achievement', 'Logro'), width: 14, style: 4 },
        { key: 'pointsLost', header: tr(translate, 'export_column_points_lost', 'Puntos perdidos'), width: 20, style: 2 },
        { key: 'applicable', header: tr(translate, 'export_column_applicable', 'Aplicable'), width: 14 },
        { key: 'code', header: tr(translate, 'export_column_code', 'Código'), width: 24 },
        { key: 'reason', header: tr(translate, 'export_column_reason', 'Motivo'), width: 55 },
      ],
      rows: dimensionRows,
    });
    sheets.push(rankingSheet(
      tr(translate, 'fs_ranking_title', 'Ranking'),
      buildRangeRanking(view.columns, view.rows, view.data, 'score'),
      view, nivelSeleccionado, translate, 'score',
    ));
    sheets.push({
      name: tr(translate, 'export_sheet_parameters', 'Parámetros'),
      columns: [
        { key: 'parameter', header: tr(translate, 'export_column_parameter', 'Parámetro'), width: 34 },
        { key: 'value', header: tr(translate, 'value', 'Valor'), width: 40 },
      ],
      rows: recalcParams
        ? Object.entries(recalcParams).flatMap(([key, value]) => (
          value && typeof value === 'object'
            ? Object.entries(value).map(([inner, innerValue]) => ({ parameter: `${key}.${inner}`, value: innerValue }))
            : [{ parameter: key, value }]
        ))
        : [{ parameter: tr(translate, 'export_column_recalculation', 'Recálculo'), value: tr(translate, 'export_original_scores', 'Valores originales del servidor, sin recálculo') }],
    });
  } else {
    const samples = detailRows.flatMap((row) => {
      const reaction = view.data?.[row.columnKey]?.[row.id]?.reaction_time;
      return (reaction?.samples_sec || []).map((latency) => ({
        entity: row.entity, period: row.period, latency: number(latency),
      }));
    });
    if (samples.length) sheets.push({
      name: tr(translate, 'export_sheet_reaction_times', 'Tiempos de reacción'),
      columns: [
        { key: 'entity', header: entityHeader(nivelSeleccionado, translate), width: 22 },
        { key: 'period', header: tr(translate, 'export_column_period', 'Periodo'), width: 18 },
        { key: 'latency', header: tr(translate, 'export_column_latency', 'Latencia (s)'), width: 16, style: 2 },
      ],
      rows: samples,
    });
    const eventRows = attentionEvents(view, translate);
    if (eventRows.length) sheets.push({
      name: tr(translate, 'export_sheet_alarm_events_fs', 'Eventos de alarma'),
      columns: [
        { key: 'entity', header: entityHeader(nivelSeleccionado, translate), width: 22 },
        { key: 'period', header: tr(translate, 'export_column_period', 'Periodo'), width: 18 },
        { key: 'visit', header: tr(translate, 'export_column_visit', 'Visita'), width: 14 },
        { key: 'time', header: tr(translate, 'hour', 'Hora'), width: 22 },
        { key: 'type', header: tr(translate, 'export_column_type', 'Tipo'), width: 16 },
        { key: 'detail', header: tr(translate, 'detail', 'Detalle'), width: 50 },
        { key: 'attended', header: tr(translate, 'export_column_attended', 'Atendido'), width: 14 },
        { key: 'natural', header: tr(translate, 'fs_event_natural_recovery', 'Recuperación natural'), width: 22 },
      ],
      rows: eventRows,
    });
    sheets.push(
      rankingSheet(tr(translate, 'export_sheet_attention_ranking', 'Ranking atención'), buildRangeRanking(view.columns, view.rows, view.data, 'attention'), view, nivelSeleccionado, translate, 'attention'),
      rankingSheet(tr(translate, 'export_sheet_reaction_ranking', 'Ranking reacción'), buildRangeRanking(view.columns, view.rows, view.data, 'reaction'), view, nivelSeleccionado, translate, 'reaction'),
    );
  }
  sheets.push(reportInfo({ mode, translate, metadata, level: nivelSeleccionado, groupMode, recalcParams }));
  return sheets;
}
